/**
 * Developer Documentation — `/docs`
 *
 * Source of truth for the public-facing Connexis API.
 * Sections (in-page anchor nav):
 *   1. Quickstart       — send your first lead in 90 seconds
 *   2. Authentication   — X-API-Key header
 *   3. Lead ingest      — POST /api/v1/leads/ingest (the headline endpoint)
 *   4. Lead schema      — field reference + sector-specific extras
 *   5. Sector pricing   — base CPLs by sector
 *   6. Webhooks         — event catalogue + HMAC signing (Stripe-style)
 *   7. Signature verification — copy-paste snippets for Node + Python
 *   8. Errors           — what each non-200 means
 *   9. Rate limits      — per-key throughput + idempotency notes
 *  10. SDKs             — coming soon
 *
 * Brand: same calm navy/slate/brand-blue language as /inside-connexis.
 * Everything here MUST match the actual code — no aspirational shapes.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Terminal, KeyRound, FileJson, Webhook,
  ShieldCheck, AlertTriangle, Gauge, Copy, Check, Sparkles,
} from "lucide-react";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

const PUBLIC_API_BASE = "https://connex.is";
const SANDBOX_KEY = "pk_test_sandbox_DO_NOT_USE_IN_PRODUCTION";

/* -------------------------------------------------------------------------- */
/*  Lead schema — must mirror backend/publishers.py LeadIngestIn               */
/* -------------------------------------------------------------------------- */
const LEAD_FIELDS = [
  { name: "sector",         type: "string",  required: true,  desc: "One of the published sector keys (see Sector pricing below)." },
  { name: "first_name",     type: "string",  required: true,  desc: "End-consumer first name." },
  { name: "last_name",      type: "string",  required: true,  desc: "End-consumer last name." },
  { name: "email",          type: "string",  required: true,  desc: "Validated email address (RFC 5322)." },
  { name: "phone",          type: "string",  required: true,  desc: "E.164 format preferred (e.g. +447700900123)." },
  { name: "city",           type: "string",  required: false, desc: "Optional — used for routing weighting." },
  { name: "postcode",       type: "string",  required: false, desc: "UK format (e.g. SW1A 1AA). Lowercased server-side." },
  { name: "consent_token",  type: "string",  required: false, desc: "Token from your consent capture flow. Stored alongside the lead in the audit trail." },
  { name: "consent_at",     type: "ISO-8601", required: false, desc: "Timestamp consent was captured. UTC recommended." },
  { name: "ip",             type: "string",  required: false, desc: "Original click/submit IP. Used for fraud + dedupe." },
  { name: "user_agent",     type: "string",  required: false, desc: "Browser user-agent string at submit time." },
  { name: "source_sub_id",  type: "string",  required: false, desc: "Your own A/B variant or campaign ID — round-tripped in webhooks." },
  { name: "extra_fields",   type: "object",  required: false, desc: "Sector-specific structured fields (mortgage value, loan amount, debt total, etc.)." },
];

/* -------------------------------------------------------------------------- */
/*  Sector base prices — mirrors backend/publishers.py SECTOR_BASE_PRICE       */
/* -------------------------------------------------------------------------- */
const SECTOR_PRICES = [
  { key: "investor-leads",      title: "Investor Leads",         base_gbp: 120 },
  { key: "wealth-management",   title: "Wealth Management",      base_gbp: 95 },
  { key: "commercial-finance",  title: "Commercial Finance",     base_gbp: 145 },
  { key: "property-investment", title: "Property Investment",    base_gbp: 88 },
  { key: "business-finance",    title: "Business Finance",       base_gbp: 52 },
  { key: "claims-management",   title: "Claims Management",      base_gbp: 42 },
  { key: "mortgages",           title: "Mortgages",              base_gbp: 38 },
  { key: "life-insurance",      title: "Life Insurance",         base_gbp: 34 },
  { key: "debt-help",           title: "Debt Help",              base_gbp: 28 },
  { key: "payday-loans",        title: "Payday Loans",           base_gbp: 14 },
];

/* -------------------------------------------------------------------------- */
/*  Webhook events — mirrors backend events emitted via enqueue_delivery       */
/* -------------------------------------------------------------------------- */
const WEBHOOK_EVENTS = [
  {
    event: "lead.purchased",
    when:  "A buyer purchases your lead from the marketplace.",
    payload_shape: `{
  "event": "lead.purchased",
  "id":    "lead_8f3b…",
  "sector":"mortgages",
  "buyer": { "id":"buy_…", "company":"…" },
  "price_gbp": 38.00,
  "purchased_at": "2026-02-09T07:42:18Z",
  "source_sub_id": "ab-variant-3"
}`,
  },
  {
    event: "lead.rejected",
    when:  "Your lead failed routing acceptance (validation, dedupe, suppression, or no-buyer-match).",
    payload_shape: `{
  "event": "lead.rejected",
  "id":    "lead_8f3b…",
  "reason":"dedupe_30d",
  "rejected_at": "2026-02-09T07:42:18Z"
}`,
  },
];

/* -------------------------------------------------------------------------- */
/*  Error codes — pull from backend HTTPException raise sites                  */
/* -------------------------------------------------------------------------- */
const ERRORS = [
  { code: 401, name: "Invalid or missing X-API-Key", note: "Header missing, key revoked, or doesn't exist." },
  { code: 403, name: "Publisher account is paused",  note: "Contact your account manager. Existing keys continue to validate but ingestion is blocked." },
  { code: 422, name: "Validation error",             note: "Pydantic schema failure — typically a required field is missing or malformed." },
  { code: 409, name: "Duplicate lead",               note: "Same email or phone within the 30-day dedupe window." },
  { code: 429, name: "Rate limited",                 note: "Per-key throughput exceeded. See Rate limits." },
  { code: 500, name: "Internal error",               note: "Unexpected — auto-retried by your client? Email ops@connex.is with the request id." },
];

/* -------------------------------------------------------------------------- */
/*  Small UI bits                                                              */
/* -------------------------------------------------------------------------- */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1400); }}
      className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-md bg-slate-800/40 hover:bg-slate-800/60 text-slate-100 px-2 py-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold transition-colors"
      data-testid="docs-copy"
    >
      {copied ? <><Check className="size-3" /> Copied</> : <><Copy className="size-3" /> Copy</>}
    </button>
  );
}

function CodeBlock({ children, lang = "bash" }) {
  const text = typeof children === "string" ? children : "";
  return (
    <div className="relative">
      <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.16em] font-mono text-slate-400">{lang}</div>
      <CopyButton text={text} />
      <pre className="rounded-xl bg-[#0B1220] text-slate-100 px-5 pt-9 pb-5 overflow-x-auto text-[12.5px] leading-relaxed font-mono border border-slate-800">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Anchor({ id }) {
  return <span id={id} className="block -mt-24 pt-24" aria-hidden="true" />;
}

function SectionTitle({ kicker, title, children }) {
  return (
    <div className="mb-7">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF]">{kicker}</div>
      <h2 className="mt-1.5 text-2xl sm:text-3xl tracking-tight font-semibold text-slate-900">{title}</h2>
      {children && <p className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-2xl">{children}</p>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sample code — kept in one place so the cURL/Node/Python all agree          */
/* -------------------------------------------------------------------------- */
const QUICKSTART_CURL = `curl -X POST ${PUBLIC_API_BASE}/api/v1/leads/ingest \\
  -H "X-API-Key: $CONNEXIS_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sector": "mortgages",
    "first_name": "James",
    "last_name": "Patel",
    "email": "j.patel@example.co.uk",
    "phone": "+447712345678",
    "postcode": "SW1A 1AA",
    "consent_token": "ct_abc123",
    "consent_at": "2026-02-09T07:42:18Z",
    "ip": "203.0.113.42",
    "extra_fields": { "property_value_gbp": 350000, "deposit_gbp": 70000 }
  }'`;

const QUICKSTART_NODE = `import fetch from "node-fetch";

const res = await fetch("${PUBLIC_API_BASE}/api/v1/leads/ingest", {
  method: "POST",
  headers: {
    "X-API-Key": process.env.CONNEXIS_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    sector: "mortgages",
    first_name: "James",
    last_name: "Patel",
    email: "j.patel@example.co.uk",
    phone: "+447712345678",
    postcode: "SW1A 1AA",
    consent_token: "ct_abc123",
    consent_at: new Date().toISOString(),
    extra_fields: { property_value_gbp: 350000, deposit_gbp: 70000 },
  }),
});

const result = await res.json();
console.log(result);`;

const QUICKSTART_PY = `import os, requests

r = requests.post(
    "${PUBLIC_API_BASE}/api/v1/leads/ingest",
    headers={"X-API-Key": os.environ["CONNEXIS_API_KEY"]},
    json={
        "sector": "mortgages",
        "first_name": "James",
        "last_name":  "Patel",
        "email":      "j.patel@example.co.uk",
        "phone":      "+447712345678",
        "postcode":   "SW1A 1AA",
        "consent_token": "ct_abc123",
        "consent_at":    "2026-02-09T07:42:18Z",
        "extra_fields": {"property_value_gbp": 350000, "deposit_gbp": 70000},
    },
    timeout=10,
)
r.raise_for_status()
print(r.json())`;

const VERIFY_NODE = `import crypto from "node:crypto";

// Verify an inbound Connexis webhook (Stripe-style HMAC).
function verifyConnexisSignature(secret, rawBody, header, toleranceSec = 300) {
  if (!header) return false;
  const parts = Object.fromEntries(header.split(",").map(kv => kv.split("=")));
  if (!parts.t || !parts.v1) return false;
  // Reject anything older than tolerance — defeats replays
  if (Math.abs(Math.floor(Date.now() / 1000) - Number(parts.t)) > toleranceSec) return false;
  const expected = crypto.createHmac("sha256", secret)
    .update(\`\${parts.t}.\${rawBody}\`).digest("hex");
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(parts.v1));
}`;

const VERIFY_PY = `import hmac, hashlib, time

def verify_connexis_signature(secret: str, raw_body: bytes, header: str, tolerance_sec: int = 300) -> bool:
    """Verify Connexis webhook signature (Stripe-style)."""
    if not header:
        return False
    parts = dict(kv.split("=", 1) for kv in header.split(",") if "=" in kv)
    t, v1 = parts.get("t"), parts.get("v1")
    if not t or not v1:
        return False
    if abs(int(time.time()) - int(t)) > tolerance_sec:
        return False  # stale or replay
    signing_input = f"{t}.".encode() + raw_body
    expected = hmac.new(secret.encode(), signing_input, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)`;

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function Docs() {
  const rain = useCodeRain("docs");
  // Smooth scroll for in-page anchor clicks
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = "auto"; };
  }, []);

  return (
    <MarketingLayout>
      <div className="relative bg-white">
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-white via-slate-50/60 to-white">
          {rain.on && <CodeRain variant="light" intensity={rain.intensity} />}
          <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-12">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF]">Developer documentation · v1</div>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] font-semibold text-slate-900 leading-[1.04] max-w-3xl">
              Build on the routing fabric.
            </h1>
            <p className="mt-5 text-base text-slate-600 max-w-2xl leading-relaxed">
              Submit leads, receive signed webhooks, reconcile payouts. The same API our own
              publishers and partner stacks run on — production-grade, versioned, and stable.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button asChild className="rounded-full h-11 px-5 bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="docs-cta-apply">
                <Link to="/publisher/apply">Get an API key &rarr;</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-11 px-5">
                <a href="#quickstart">Read the quickstart</a>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Sparkles className="size-3.5 text-[#0055FF]" /> Stable v1 since 2025</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-3.5 text-[#0055FF]" /> HMAC-signed webhooks (Stripe-style)</span>
              <span className="inline-flex items-center gap-1.5"><Gauge className="size-3.5 text-[#0055FF]" /> 99.99% uptime SLA</span>
            </div>
          </div>
        </section>

        {/* TWO-COLUMN BODY: STICKY NAV + CONTENT */}
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-x-12 gap-y-10">
          {/* In-page nav */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-3">On this page</div>
            <nav className="text-[13px] space-y-1.5">
              {[
                ["quickstart", "Quickstart"],
                ["authentication", "Authentication"],
                ["lead-ingest", "Lead ingest"],
                ["lead-schema", "Lead schema"],
                ["sector-pricing", "Sector pricing"],
                ["webhooks", "Webhooks"],
                ["verify-signature", "Verifying signatures"],
                ["errors", "Errors"],
                ["rate-limits", "Rate limits"],
                ["sdks", "SDKs"],
                ["trust-widget", "Trust widget"],
              ].map(([id, label]) => (
                <a key={id} href={`#${id}`} className="block text-slate-600 hover:text-[#0055FF] transition-colors" data-testid={`docs-toc-${id}`}>
                  {label}
                </a>
              ))}
            </nav>
            <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
              <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-500">Need help?</div>
              <p className="mt-1.5 text-[12.5px] text-slate-600 leading-relaxed">
                <a href="mailto:ops@connex.is" className="text-[#0055FF] hover:underline">ops@connex.is</a> · responses within 1 business day.
              </p>
            </div>
          </aside>

          <main className="space-y-20">
            {/* ----- Quickstart ----- */}
            <section>
              <Anchor id="quickstart" />
              <SectionTitle kicker="01 · Quickstart" title="Send your first lead in 90 seconds">
                Three steps: get approved, grab your API key, send a POST. No SDK install required.
              </SectionTitle>
              <ol className="space-y-3 text-[14px] text-slate-700 mb-7">
                <li><span className="font-semibold text-slate-900">1.</span> <Link to="/publisher/apply" className="text-[#0055FF] hover:underline">Apply for publisher access</Link>. Admin approves, you receive your API key by email.</li>
                <li><span className="font-semibold text-slate-900">2.</span> Set it as an env var: <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-[12px]">export CONNEXIS_API_KEY=pk_live_&hellip;</code></li>
                <li><span className="font-semibold text-slate-900">3.</span> POST your first lead with the sample below — accepted leads land in your dashboard immediately.</li>
              </ol>

              {/* iter90w — public sandbox key callout. Lets visitors curl-test without applying. */}
              <div className="mb-6 rounded-xl border border-[#0055FF]/30 bg-gradient-to-r from-[#0055FF]/[0.04] via-white to-white px-5 py-4" data-testid="docs-sandbox-callout">
                <div className="flex items-start gap-3">
                  <Terminal className="size-4 text-[#0055FF] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900">Try it now — no account required.</div>
                    <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                      Use the public sandbox key below to send a test lead and see the exact response
                      shape. Sandbox requests are never stored, routed, billed, or surfaced to buyers —
                      they&apos;re purely for client integration testing.
                    </p>
                    <div className="mt-2.5">
                      <code className="inline-block font-mono text-[11.5px] text-[#0055FF] bg-white border border-slate-200 rounded-md px-2.5 py-1.5 select-all" data-testid="docs-sandbox-key">
                        {SANDBOX_KEY}
                      </code>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">cURL</div>
                  <CodeBlock lang="bash">{QUICKSTART_CURL}</CodeBlock>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Node.js</div>
                  <CodeBlock lang="javascript">{QUICKSTART_NODE}</CodeBlock>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Python</div>
                  <CodeBlock lang="python">{QUICKSTART_PY}</CodeBlock>
                </div>
              </div>

              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3 text-[12.5px] text-slate-600">
                <span className="font-medium text-slate-900">Successful response:</span> <code className="text-slate-800">{`{ "ok": true, "lead_id": "lead_…", "status": "accepted" | "queued" | "rejected", "reason": null }`}</code>
              </div>
            </section>

            {/* ----- Authentication ----- */}
            <section>
              <Anchor id="authentication" />
              <SectionTitle kicker="02 · Authentication" title="Single header. No OAuth dance.">
                Every request must carry your secret API key in the <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-[12px]">X-API-Key</code> header.
                Keys are issued per publisher account; rotate from <Link to="/publisher" className="text-[#0055FF] hover:underline">your publisher dashboard</Link>.
              </SectionTitle>
              <CodeBlock lang="bash">{`X-API-Key: cnx_live_a1b2c3d4e5f6g7h8…`}</CodeBlock>
              <ul className="mt-4 space-y-1.5 text-[13px] text-slate-600">
                <li>· Treat the key like a password — never commit it, never paste into client-side JS</li>
                <li>· Rotate immediately if exposed; old key is revoked the moment a new one is generated</li>
                <li>· Bearer / Authorization headers are <em>not</em> accepted on ingest — must be <code className="px-1 rounded bg-slate-100 text-slate-800 text-[11.5px]">X-API-Key</code></li>
              </ul>
            </section>

            {/* ----- Lead ingest ----- */}
            <section>
              <Anchor id="lead-ingest" />
              <SectionTitle kicker="03 · Endpoint reference" title="POST · /api/v1/leads/ingest">
                The headline endpoint. Submits a single end-consumer lead into the Connexis routing
                fabric. Returns synchronously with an accepted / queued / rejected verdict in &lt; 500ms typical.
              </SectionTitle>
              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-[13px]">
                  <tbody className="divide-y divide-slate-100">
                    <tr><td className="px-4 py-2.5 bg-slate-50/60 font-mono text-slate-600 w-44">Method</td><td className="px-4 py-2.5 font-mono text-slate-900">POST</td></tr>
                    <tr><td className="px-4 py-2.5 bg-slate-50/60 font-mono text-slate-600">URL</td><td className="px-4 py-2.5 font-mono text-slate-900">{PUBLIC_API_BASE}/api/v1/leads/ingest</td></tr>
                    <tr><td className="px-4 py-2.5 bg-slate-50/60 font-mono text-slate-600">Auth</td><td className="px-4 py-2.5 font-mono text-slate-900">X-API-Key: &lt;your_key&gt;</td></tr>
                    <tr><td className="px-4 py-2.5 bg-slate-50/60 font-mono text-slate-600">Content-Type</td><td className="px-4 py-2.5 font-mono text-slate-900">application/json</td></tr>
                    <tr><td className="px-4 py-2.5 bg-slate-50/60 font-mono text-slate-600">Idempotency</td><td className="px-4 py-2.5 font-mono text-slate-900">Dedupe by email+phone (30-day window)</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* ----- Lead schema ----- */}
            <section>
              <Anchor id="lead-schema" />
              <SectionTitle kicker="04 · Lead schema" title="Request body fields">
                Required fields are bolded. Anything not listed below should go inside{" "}
                <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-[12px]">extra_fields</code>{" "}
                — we round-trip the full object back to buyers, so sector-specific data is preserved.
              </SectionTitle>
              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                    <tr>
                      <th className="text-left px-4 py-2.5">Field</th>
                      <th className="text-left px-4 py-2.5 w-28">Type</th>
                      <th className="text-left px-4 py-2.5 w-24">Required</th>
                      <th className="text-left px-4 py-2.5">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {LEAD_FIELDS.map((f) => (
                      <tr key={f.name} className="hover:bg-slate-50/40">
                        <td className="px-4 py-2.5 font-mono text-slate-900">{f.name}</td>
                        <td className="px-4 py-2.5 font-mono text-slate-600">{f.type}</td>
                        <td className="px-4 py-2.5">{f.required ? <span className="text-[#0055FF] font-semibold">Yes</span> : <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-2.5 text-slate-600">{f.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ----- Sector pricing ----- */}
            <section>
              <Anchor id="sector-pricing" />
              <SectionTitle kicker="05 · Sector pricing" title="Base CPLs by sector">
                These are the default minimum prices we route at. Actual paid CPL can be higher
                when a buyer holds a premium rule for the geo/score/source combination.
              </SectionTitle>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {SECTOR_PRICES.map((s) => (
                  <div key={s.key} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-medium text-slate-900">{s.title}</div>
                      <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-slate-500 mt-0.5">{s.key}</div>
                    </div>
                    <div className="text-[15px] font-semibold tabular-nums text-[#0055FF]">£{s.base_gbp}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* ----- Webhooks ----- */}
            <section>
              <Anchor id="webhooks" />
              <SectionTitle kicker="06 · Webhooks" title="Server-to-server event delivery">
                Configure a webhook URL on your publisher account and we&apos;ll POST signed JSON to it
                when downstream events fire. Stripe-style retry schedule: 30s &rarr; 2m &rarr; 10m &rarr; 1h &rarr; 4h,
                5 attempts before giving up.
              </SectionTitle>
              <div className="space-y-6">
                {WEBHOOK_EVENTS.map((evt) => (
                  <div key={evt.event} className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50/60 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <code className="text-[13px] font-mono font-semibold text-slate-900">{evt.event}</code>
                        <p className="text-[12px] text-slate-500 mt-0.5">{evt.when}</p>
                      </div>
                      <Webhook className="size-4 text-[#0055FF]" />
                    </div>
                    <div className="bg-[#0B1220]">
                      <pre className="px-5 py-4 text-[12px] leading-relaxed font-mono text-slate-100 overflow-x-auto"><code>{evt.payload_shape}</code></pre>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3 text-[12.5px] text-slate-600 leading-relaxed">
                <span className="font-medium text-slate-900">Signature header:</span>{" "}
                <code className="font-mono text-slate-800">X-Connexis-Signature: t=&lt;unix_ts&gt;,v1=&lt;hex_sha256&gt;</code>.
                Verify before processing — see next section for working snippets.
              </div>
            </section>

            {/* ----- Verify signature ----- */}
            <section>
              <Anchor id="verify-signature" />
              <SectionTitle kicker="07 · Verifying signatures" title="Drop-in code (no SDK required)">
                We sign the request body with HMAC-SHA256 against your per-publisher{" "}
                <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-[12px]">webhook_secret</code> (rotate from your dashboard).
                Reject anything with a stale timestamp (&gt; 5 minutes) to defeat replay attacks.
              </SectionTitle>
              <div className="space-y-5">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Node.js</div>
                  <CodeBlock lang="javascript">{VERIFY_NODE}</CodeBlock>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">Python</div>
                  <CodeBlock lang="python">{VERIFY_PY}</CodeBlock>
                </div>
              </div>
            </section>

            {/* ----- Errors ----- */}
            <section>
              <Anchor id="errors" />
              <SectionTitle kicker="08 · Errors" title="HTTP status reference">
                Every non-2xx response carries a JSON body with{" "}
                <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-[12px]">detail</code> set to a
                human-readable explanation. Treat 5xx as transient — retry with backoff. Treat 4xx as a
                client bug to fix.
              </SectionTitle>
              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                    <tr>
                      <th className="text-left px-4 py-2.5 w-20">Status</th>
                      <th className="text-left px-4 py-2.5">Reason</th>
                      <th className="text-left px-4 py-2.5">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ERRORS.map((e) => (
                      <tr key={e.code} className="hover:bg-slate-50/40">
                        <td className="px-4 py-2.5 font-mono font-semibold text-slate-900">{e.code}</td>
                        <td className="px-4 py-2.5 text-slate-900">{e.name}</td>
                        <td className="px-4 py-2.5 text-slate-600">{e.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ----- Rate limits ----- */}
            <section>
              <Anchor id="rate-limits" />
              <SectionTitle kicker="09 · Rate limits & idempotency" title="What's enforced">
                Defaults are generous; bespoke ceilings available on request. If you&apos;re bursting,
                space submissions out evenly rather than batching — the routing fabric prefers a steady stream.
              </SectionTitle>
              <ul className="space-y-2.5 text-[13px] text-slate-700">
                <li className="flex gap-2.5"><Gauge className="size-3.5 text-[#0055FF] mt-1 shrink-0" /> <span><span className="font-semibold text-slate-900">120 req/min</span> per API key on the ingest endpoint. 429 returned when exceeded.</span></li>
                <li className="flex gap-2.5"><Gauge className="size-3.5 text-[#0055FF] mt-1 shrink-0" /> <span><span className="font-semibold text-slate-900">30-day dedupe window</span> by (email OR phone) within your publisher account. A retry of an already-accepted lead returns 409.</span></li>
                <li className="flex gap-2.5"><Gauge className="size-3.5 text-[#0055FF] mt-1 shrink-0" /> <span><span className="font-semibold text-slate-900">Webhook retries</span>: 30s &rarr; 2m &rarr; 10m &rarr; 1h &rarr; 4h, 5 attempts.</span></li>
              </ul>
            </section>

            {/* ----- SDKs ----- */}
            <section>
              <Anchor id="sdks" />
              <SectionTitle kicker="10 · SDKs" title="Drop-in client libraries">
                Tiny, single-file SDKs with retries, validation, and typed errors. MIT-licensed, dependency-free
                (Node uses built-in <code className="font-mono text-[12px]">fetch</code>, Python uses <code className="font-mono text-[12px]">requests</code>).
                Drop the file into your project and you&apos;re submitting leads in 5 lines.
              </SectionTitle>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {/* Node */}
                <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="size-7 rounded-md bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="size-4 text-emerald-600" fill="currentColor"><path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.4l7.5 3.75L12 11.9 4.5 8.15 12 4.4zm-8 5.25l7 3.5v7.7l-7-3.5V9.65zm9 11.2v-7.7l7-3.5v7.7l-7 3.5z"/></svg>
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900">Node.js</div>
                    <span className="ml-auto text-[10.5px] font-mono uppercase tracking-wider text-slate-400">v1.0.0</span>
                  </div>
                  <p className="text-[12.5px] text-slate-600 mb-3 leading-snug">
                    Works on Node 18+ and modern browsers. CommonJS + ESM dual export.
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href="/sdk/connexis-node.js"
                      download
                      className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold transition-colors"
                      data-testid="docs-sdk-download-node"
                    >
                      Download SDK
                      <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </a>
                    <a
                      href="/sdk/connexis-node.js"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[12px] text-[#0055FF] hover:underline"
                      data-testid="docs-sdk-view-node"
                    >
                      View source →
                    </a>
                  </div>
                  <pre className="mt-3 rounded-md bg-slate-950 text-slate-100 p-3 text-[11px] leading-relaxed font-mono overflow-x-auto whitespace-pre">{`const { Connexis } = require("./connexis-node.js");

const client = new Connexis({ apiKey: process.env.CONNEXIS_API_KEY });
const res = await client.ingest({
  sector: "mortgages", first_name: "Olivia", last_name: "Hughes",
  email: "olivia@example.com", phone: "+447900654321",
});
console.log(res.lead_id, res.price_gbp);`}</pre>
                </div>

                {/* Python */}
                <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="size-7 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="size-4 text-blue-600" fill="currentColor"><path d="M11.7 1.5c-3 0-5.4.6-5.4 3v2.6h5.5v.6H4.5C2 7.7 1 9.9 1 12.5s1 4.8 3.5 4.8h1.5v-3c0-2.4 2.1-4.4 4.7-4.4h4.5c2 0 3.7-1.6 3.7-3.6V4.5c0-2-1.7-3-3.7-3-.8 0-2.3 0-3.5 0zm-1.4 1.2c.5 0 1 .4 1 1s-.5 1-1 1-1-.5-1-1 .5-1 1-1zM18.4 7v3c0 2.4-2.1 4.4-4.7 4.4H9.2c-2 0-3.7 1.6-3.7 3.6v3c0 2 1.7 3 3.7 3 .8 0 2.3 0 3.5 0 3 0 5.4-.6 5.4-3v-2.6h-5.5v-.6H19.5c2.5 0 3.5-2.2 3.5-4.8s-1-4.8-3.5-4.8h-1.1zm-4.7 13c.5 0 1 .4 1 1s-.5 1-1 1-1-.5-1-1 .5-1 1-1z"/></svg>
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900">Python</div>
                    <span className="ml-auto text-[10.5px] font-mono uppercase tracking-wider text-slate-400">v1.0.0</span>
                  </div>
                  <p className="text-[12.5px] text-slate-600 mb-3 leading-snug">
                    Single file, requires only <code className="font-mono">requests</code>. Compatible with Python 3.8+.
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href="/sdk/connexis_sdk.py"
                      download
                      className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-semibold transition-colors"
                      data-testid="docs-sdk-download-python"
                    >
                      Download SDK
                      <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </a>
                    <a
                      href="/sdk/connexis_sdk.py"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[12px] text-[#0055FF] hover:underline"
                      data-testid="docs-sdk-view-python"
                    >
                      View source →
                    </a>
                  </div>
                  <pre className="mt-3 rounded-md bg-slate-950 text-slate-100 p-3 text-[11px] leading-relaxed font-mono overflow-x-auto whitespace-pre">{`from connexis_sdk import Connexis

client = Connexis(api_key=os.environ["CONNEXIS_API_KEY"])
res = client.ingest({
    "sector": "mortgages", "first_name": "Olivia", "last_name": "Hughes",
    "email": "olivia@example.com", "phone": "+447900654321",
})
print(res["lead_id"], res["price_gbp"])`}</pre>
                </div>
              </div>

              <p className="mt-4 text-[12px] text-slate-500">
                Official npm & PyPI packages land Q2 2026. Until then, the source files above are versioned & production-ready —
                you can vendor them into your repo or wrap them in your own internal package. Bugs?{" "}
                <a href="mailto:ops@connex.is" className="text-[#0055FF] hover:underline">ops@connex.is</a>.
              </p>
            </section>

            {/* ----- 11 · Trust widget (Iter98) ----- */}
            <section>
              <Anchor id="trust-widget" />
              <SectionTitle kicker="11 · Trust widget" title="Show off live market activity">
                Anonymous, zero-PII trust badge built on top of the same routing engine. Embed it on your own marketing site,
                publisher portal, or partner landing pages to prove the Connexis market is alive. Updates every 60 seconds.
              </SectionTitle>

              <TrustWidgetPreview />
            </section>

            {/* CTA at the end */}
            <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0055FF]/[0.04] via-white to-white px-6 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl tracking-tight font-semibold text-slate-900">Ready to start?</h3>
                <p className="text-[13.5px] text-slate-600 mt-1">Apply for publisher access &mdash; admin reviews within one business day.</p>
              </div>
              <Button asChild className="rounded-full h-11 px-5 bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="docs-cta-footer">
                <Link to="/publisher/apply">Get an API key <ArrowRight className="size-4" /></Link>
              </Button>
            </section>
          </main>
        </div>
      </div>
    </MarketingLayout>
  );
}


/* ─────────────────────────  Iter98 — Trust widget preview + copy block  ───────────────────────── */

function TrustWidgetPreview() {
  const [copied, setCopied] = React.useState(null);
  const [tab, setTab] = React.useState("iframe");
  const [theme, setTheme] = React.useState("light");
  const [accent, setAccent] = React.useState("#0055FF");
  const [compact, setCompact] = React.useState(false);

  const base = process.env.REACT_APP_BACKEND_URL || "https://connex.is";
  const origin = base.replace(/\/api\/?$/, "").replace(/\/+$/, "");

  // Build the URL query params from current theming
  const params = new URLSearchParams();
  if (theme !== "light") params.set("theme", theme);
  if (accent !== "#0055FF") params.set("accent", accent.replace(/^#/, ""));
  if (compact) params.set("compact", "1");
  const qs = params.toString();
  const iframeSrc = `${origin}/embed/sector-stats${qs ? "?" + qs : ""}`;
  const widgetScript = `${origin}/embed/connexis-widget.js`;
  const widgetHeight = compact ? 240 : 480;

  const iframeCode = `<iframe src="${iframeSrc}" width="100%" height="${widgetHeight}" frameborder="0" title="Connexis live market" style="border:1px solid #e2e8f0;border-radius:12px;background:#fff;max-width:680px;"></iframe>`;

  // JS-loader code (no iframe — works on Webflow/Squarespace/plain HTML)
  const dataAttrs = [
    'data-connexis-widget="sector-stats"',
    theme !== "light"      ? `data-theme="${theme}"` : null,
    accent !== "#0055FF"   ? `data-accent="${accent}"` : null,
    compact                ? 'data-compact="true"' : null,
  ].filter(Boolean).join("\n     ");
  const jsCode = `<div ${dataAttrs}></div>
<script src="${widgetScript}" defer></script>`;

  const copy = async (key, txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch (_) { /* ignore */ }
  };

  const currentCode = tab === "iframe" ? iframeCode : jsCode;

  return (
    <div className="mt-6 grid lg:grid-cols-5 gap-5 items-start">
      {/* Live preview */}
      <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-slate-50/50 overflow-hidden self-start">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-white flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Live preview</div>

          {/* Theming controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              {["light", "dark"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTheme(t)}
                  className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${theme === t ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`docs-trust-theme-${t}`}
                >
                  {t === "light" ? "Light" : "Dark"}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
              Accent
              <input
                type="color"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                className="size-6 rounded border border-slate-200 cursor-pointer p-0 bg-white"
                data-testid="docs-trust-accent"
              />
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={compact}
                onChange={(e) => setCompact(e.target.checked)}
                data-testid="docs-trust-compact"
              />
              Compact
            </label>
            <a
              href={iframeSrc}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11.5px] text-[#0055FF] hover:underline inline-flex items-center gap-1"
              data-testid="docs-trust-widget-open"
            >
              Open ↗
            </a>
          </div>
        </div>
        <iframe
          key={iframeSrc}
          src={iframeSrc}
          width="100%"
          height={widgetHeight}
          frameBorder="0"
          title="Connexis live market"
          style={{ border: 0, background: theme === "dark" ? "#0a0f1c" : "#fff", display: "block" }}
          data-testid="docs-trust-widget-iframe"
        />
      </div>

      {/* Embed code */}
      <div className="lg:col-span-2 space-y-4">
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-2.5">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Embed code</div>
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              {["iframe", "js"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${tab === t ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`docs-trust-embed-tab-${t}`}
                >
                  {t === "iframe" ? "iframe" : "JS / no-iframe"}
                </button>
              ))}
            </div>
          </div>

          <div className="relative rounded-md border border-slate-800 bg-slate-950 overflow-hidden">
            <button
              type="button"
              onClick={() => copy(tab, currentCode)}
              className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 rounded h-7 px-2.5 bg-slate-800/80 hover:bg-slate-700 text-[11px] font-semibold text-slate-100 z-10 transition-colors"
              data-testid="docs-trust-widget-copy"
            >
              {copied === tab ? "Copied" : "Copy"}
            </button>
            <pre className="font-mono text-[11px] leading-[1.55] text-slate-100 p-3 pr-20 overflow-x-auto whitespace-pre-wrap break-all">{currentCode}</pre>
          </div>

          <p className="mt-3 text-[11.5px] text-slate-500 leading-snug">
            {tab === "iframe"
              ? <>Drop into any iframe-friendly surface — your marketing site, partner page, Confluence, Notion, etc.</>
              : <>Use this on Webflow / Squarespace / WordPress / any CMS that lets you paste a <code className="font-mono">&lt;script&gt;</code> tag. No iframe needed.</>}
            {" "}Updates every 60 seconds. Zero-PII.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 text-[12.5px] text-slate-600 leading-relaxed">
          <div className="text-[13px] font-semibold text-slate-900 mb-2">What&apos;s shown</div>
          <ul className="space-y-1.5">
            <li><span className="text-slate-900 font-mono text-[11.5px]">Leads · 24h</span> — total accepted across all sectors</li>
            <li><span className="text-slate-900 font-mono text-[11.5px]">Active publishers</span> — distinct sources sending in the last 24h</li>
            <li><span className="text-slate-900 font-mono text-[11.5px]">Fill rate</span> — accepted / (accepted + matched-but-rejected)</li>
            <li><span className="text-slate-900 font-mono text-[11.5px]">Per-sector base price &amp; volume</span> — sorted by busiest first</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
